import { useState, useEffect } from "react";
import { ContextVersion, VersionType } from "../contexts/version.context";

const VERSION_STORAGE_KEY = 'infrasol_web_version';

// CRÍTICO: Limpiar v1 del localStorage INMEDIATAMENTE al cargar el módulo
// Esto se ejecuta ANTES de cualquier otro código que pueda establecer v1
if (typeof window !== 'undefined') {
    const stored = localStorage.getItem(VERSION_STORAGE_KEY);
    if (stored === 'v1') {
        localStorage.removeItem(VERSION_STORAGE_KEY);
        // DEBUG: Log para identificar cuándo se limpia v1
        if (process.env.NODE_ENV === 'development') {
            console.warn('[Version Provider] ⚠️ v1 detectado y eliminado del localStorage al cargar el módulo');
        }
    }
    // SIEMPRE forzar v2 si no existe o es v1
    if (!stored || stored === 'v1') {
        localStorage.setItem(VERSION_STORAGE_KEY, 'v2');
        if (process.env.NODE_ENV === 'development') {
            console.log('[Version Provider] v2 forzado en localStorage al cargar el módulo');
        }
    }
}

// Función para obtener versión inicial - SIEMPRE v2 por defecto
const getInitialVersion = (): VersionType => {
    if (typeof window === 'undefined') {
        return 'v2';
    }
    
    // 1. Verificar query parameter (solo si es v1 explícitamente)
    const urlParams = new URLSearchParams(window.location.search);
    const versionParam = urlParams.get('version') as VersionType;
    if (versionParam === 'v1') {
        return 'v1';
    }
    
    // 2. SIEMPRE v2 por defecto (ignorar cualquier otra cosa)
    return 'v2';
};

export const ProviderVersion = ({ children }: any) => {
    // Inicializar SIEMPRE con v2 - usar getInitialVersion para consistencia
    const [version, setVersionState] = useState<VersionType>(() => {
        // DEBUG: Log detallado de inicialización
        if (typeof window !== 'undefined') {
            console.log('[Version Provider] 🚀 Inicializando ProviderVersion');
            console.log('  - Ancho de ventana:', window.innerWidth);
            console.log('  - URL:', window.location.href);
            
            const urlParams = new URLSearchParams(window.location.search);
            const versionParam = urlParams.get('version') as VersionType;
            console.log('  - Query parameter version:', versionParam);
            
            const stored = localStorage.getItem(VERSION_STORAGE_KEY);
            console.log('  - localStorage antes de limpiar:', stored);
            
            if (versionParam === 'v1') {
                console.log('[Version Provider] ⚠️ v1 permitido por query parameter explícito');
                return 'v1';
            }
            
            // Limpiar v1 del localStorage
            if (stored === 'v1') {
                console.warn('[Version Provider] 🧹 Limpiando v1 del localStorage en inicialización');
                localStorage.removeItem(VERSION_STORAGE_KEY);
            }
            
            // Forzar v2
            localStorage.setItem(VERSION_STORAGE_KEY, 'v2');
            console.log('[Version Provider] ✅ v2 forzado en localStorage durante inicialización');
            console.log('  - localStorage después:', localStorage.getItem(VERSION_STORAGE_KEY));
        }
        return 'v2';
    });

    const setVersion = (newVersion: VersionType) => {
        // DEBUG: Log DETALLADO para identificar cambios no deseados
        if (typeof window !== 'undefined') {
            console.log('[Version Provider] 🔍 setVersion llamado');
            console.log('  - Nueva versión solicitada:', newVersion);
            console.log('  - Versión actual en estado:', version);
            console.log('  - Versión en localStorage:', localStorage.getItem(VERSION_STORAGE_KEY));
            console.log('  - URL actual:', window.location.href);
            console.log('  - Ancho de ventana:', window.innerWidth);
            
            if (newVersion === 'v1' && version === 'v2') {
                console.error('[Version Provider] ⚠️⚠️⚠️ CAMBIO DE v2 A v1 DETECTADO ⚠️⚠️⚠️');
                console.error('  - Stack trace completo:');
                console.trace();
                console.error('  - Caller info:');
                console.error('    * URL:', window.location.href);
                console.error('    * Width:', window.innerWidth);
                console.error('    * User Agent:', navigator.userAgent);
            }
            
            // BLOQUEAR cualquier intento de establecer v1 a menos que sea explícito
            if (newVersion === 'v1') {
                const urlParams = new URLSearchParams(window.location.search);
                const versionParam = urlParams.get('version');
                if (versionParam !== 'v1') {
                    console.error('[Version Provider] 🚫 BLOQUEADO: Intento de establecer v1 sin query parameter');
                    console.error('  - Query parameter version:', versionParam);
                    console.error('  - Forzando v2 en su lugar...');
                    newVersion = 'v2';
                }
            }
        }
        
        // Prevenir que se establezca v1 automáticamente
        // Solo permitir v1 si se establece explícitamente desde fuera del provider
        setVersionState(newVersion);
        localStorage.setItem(VERSION_STORAGE_KEY, newVersion);
        
        if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
            console.log('[Version Provider] ✅ Versión establecida:', newVersion);
        }
    };

    // Efecto para sincronizar con query parameter y forzar v2
    useEffect(() => {
        // DEBUG: Log DETALLADO para identificar cambios no deseados
        if (typeof window !== 'undefined') {
            console.log('[Version Provider] 🔄 useEffect ejecutado');
            console.log('  - Versión actual en estado:', version);
            console.log('  - Ancho de ventana:', window.innerWidth);
            console.log('  - URL:', window.location.href);
        }
        
        // Limpiar v1 del localStorage si existe
        const stored = localStorage.getItem(VERSION_STORAGE_KEY);
        if (typeof window !== 'undefined') {
            console.log('  - localStorage en useEffect:', stored);
        }
        
        if (stored === 'v1') {
            console.error('[Version Provider] ⚠️⚠️⚠️ v1 detectado en localStorage en useEffect, limpiando...');
            localStorage.removeItem(VERSION_STORAGE_KEY);
            localStorage.setItem(VERSION_STORAGE_KEY, 'v2');
            console.log('[Version Provider] ✅ v2 restaurado en localStorage');
        }
        
        // Verificar query parameter
        const urlParams = new URLSearchParams(window.location.search);
        const versionParam = urlParams.get('version') as VersionType;
        
        if (typeof window !== 'undefined') {
            console.log('  - Query parameter version:', versionParam);
        }
        
        if (versionParam === 'v1') {
            // Solo permitir v1 si está explícitamente en la URL
            console.log('[Version Provider] ⚠️ v1 permitido por query parameter explícito en useEffect');
            setVersionState('v1');
            localStorage.setItem(VERSION_STORAGE_KEY, 'v1');
        } else {
            // SIEMPRE v2 por defecto - NUNCA cambiar a v1 automáticamente
            if (version !== 'v2') {
                console.error('[Version Provider] ⚠️⚠️⚠️ Versión NO es v2 en useEffect, forzando v2');
                console.error('  - Versión actual:', version);
                console.error('  - Stack trace:');
                console.trace();
                setVersionState('v2');
                localStorage.setItem(VERSION_STORAGE_KEY, 'v2');
            } else {
                // Asegurar que localStorage también tenga v2
                const currentStored = localStorage.getItem(VERSION_STORAGE_KEY);
                if (currentStored !== 'v2') {
                    console.warn('[Version Provider] ⚠️ localStorage no tiene v2, corrigiendo...');
                    localStorage.setItem(VERSION_STORAGE_KEY, 'v2');
                }
            }
        }
        
        // Escuchar cambios en la URL
        const checkVersion = () => {
            if (typeof window !== 'undefined') {
                console.log('[Version Provider] 🔍 popstate detectado');
                console.log('  - URL:', window.location.href);
            }
            
            const newParams = new URLSearchParams(window.location.search);
            const newVersionParam = newParams.get('version') as VersionType;
            
            if (newVersionParam === 'v1') {
                console.log('[Version Provider] ⚠️ v1 permitido por query parameter (popstate)');
                setVersionState('v1');
                localStorage.setItem(VERSION_STORAGE_KEY, 'v1');
            } else {
                // Por defecto siempre v2 - NUNCA cambiar a v1 automáticamente
                const currentStored = localStorage.getItem(VERSION_STORAGE_KEY);
                if (currentStored === 'v1') {
                    console.error('[Version Provider] ⚠️⚠️⚠️ v1 detectado en localStorage después de popstate, corrigiendo...');
                    localStorage.removeItem(VERSION_STORAGE_KEY);
                    localStorage.setItem(VERSION_STORAGE_KEY, 'v2');
                }
                
                if (version !== 'v2') {
                    console.error('[Version Provider] ⚠️⚠️⚠️ Versión no es v2 después de popstate, forzando v2');
                    setVersionState('v2');
                    localStorage.setItem(VERSION_STORAGE_KEY, 'v2');
                }
            }
        };
        
        window.addEventListener('popstate', checkVersion);
        
        // MONITOREO CONTINUO: Verificar cada segundo si la versión cambió a v1
        const monitorInterval = setInterval(() => {
            const currentStored = localStorage.getItem(VERSION_STORAGE_KEY);
            if (currentStored === 'v1') {
                console.error('[Version Provider] ⚠️⚠️⚠️ MONITOR: v1 detectado en localStorage, corrigiendo...');
                console.error('  - Timestamp:', new Date().toISOString());
                console.error('  - URL:', window.location.href);
                console.error('  - Width:', window.innerWidth);
                localStorage.removeItem(VERSION_STORAGE_KEY);
                localStorage.setItem(VERSION_STORAGE_KEY, 'v2');
                if (version !== 'v2') {
                    setVersionState('v2');
                }
            }
        }, 1000); // Verificar cada segundo
        
        return () => {
            window.removeEventListener('popstate', checkVersion);
            clearInterval(monitorInterval);
        };
    }, [version]);

    return (
        <ContextVersion.Provider value={{ version, setVersion }}>
            {children}
        </ContextVersion.Provider>
    );
};

